import CancelIcon from "@mui/icons-material/Clear";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { Button, InputAdornment } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchEmployee } from "../api/employee";
import { generateTradeDetailReport } from "../api/report";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import { sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  ALL_STATUSES,
  ALL_TRADE_TYPES,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  EMPLOYEE_TRADE_TYPES,
} from "../util/constants.js";
import { THREE_YEARS_IN_PAST } from "../util/dateHelper";
import { convert_Luxon_To_DateFns, set_time_to_end_of_day } from "../util/dateTimeFormatter.js";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import { validateEndDateFilter, validateStartDateFilter } from "../util/inputValidation.js";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE,
} from "../util/messageConstants";
import { formatEmployeeIdFromKronos } from "../util/miscFormatter";
import {
  validateAdminDataForGeneratingReport,
  validateAdminId,
  validateDateType,
  validateEmployeeDataForGeneratingReport,
  validateEmployeeTradeRoleList,
  validateReportEmployeeId,
  validateReportShiftDate,
  validateStatusList,
  validateTradeTypeList,
  validateWorkgroupList,
} from "../util/reportInputValidation";
import { getWorkgroupGivenJobCode } from "../util/workgroupMapper.js";
import { getTimeZoneFromSwaStation } from "../util/timeZoneService";
import GenerateReportDownloadDialog from "./GenerateReportDownloadDialog";
import ConfirmationDialog from "./fundamentals/ConfirmationDialog";
import StarsDatePicker from "./fundamentals/StarsDatePicker";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice.js";
import { getTradeWorkgroups } from "../util/tradeUtils.js";

const useStyles = makeStyles((theme) => ({
  control: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  spinner: {
    marginTop: theme.spacing(1),
  },
  input: {
    width: "348px",
  },
  filter: {
    width: "348px",
  },
  link: {
    display: "none",
  },
}));

const TradeDetailReport = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const workgroupData = useSelector(selectWorkgroupData);

  // Filter Input State
  const [dateType, setDateType] = useState("");
  const [dateTypeErrors, setDateTypeErrors] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [startDateErrors, setStartDateErrors] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [endDateErrors, setEndDateErrors] = useState("");
  const [tradeTypeList, setTradeTypeList] = useState([]);
  const [tradeTypeListErrors, setTradeTypeListErrors] = useState("");
  const [statusList, setStatusList] = useState([]);
  const [statusListErrors, setStatusListErrors] = useState("");
  const [workgroupList, setWorkgroupList] = useState([]);
  const [workgroupListErrors, setWorkgroupListErrors] = useState("");
  const [employeeTradeRoleList, setEmployeeTradeRoleList] = useState([]);
  const [employeeTradeRoleListErrors, setEmployeeTradeRoleListErrors] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [isEmployeeIdTouched, setIsEmployeeIdTouched] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [isEmployeeDataLoading, setIsEmployeeDataLoading] = useState(false);
  const [adminId, setAdminId] = useState("");
  const [adminIdErrors, setAdminIdErrors] = useState("");
  const [adminEmployeeData, setAdminEmployeeData] = useState(null);
  const [isAdminEmployeeDataLoading, setIsAdminEmployeeDataLoading] = useState(false);

  // Generating Report State
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(false);
  const [isGenerateConfirmDialogOpen, setIsGenerateConfirmDialogOpen] = useState(false);
  const [isGenerateDownloadDialogOpen, setIsGenerateDownloadDialogOpen] = useState(false);
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [isReportDownloadError, setIsReportDownloadError] = useState(false);
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const linkRef = useRef();

  useEffect(() => {
    if (startDate) {
      const startDateUpdated = startDate.setZone(getTimeZoneFromSwaStation(props.station), {
        keepLocalTime: true,
      });

      setStartDate(startDateUpdated);
    }

    if (endDate) {
      const endDateUpdated = endDate.setZone(getTimeZoneFromSwaStation(props.station), {
        keepLocalTime: true,
      });

      setEndDate(endDateUpdated);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.station]);

  useEffect(() => {
    validateStartDateWithEndDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const validateStartDateWithEndDate = () => {
    if (startDate !== null && endDate !== null) {
      const newStartDateFilterErrors = validateStartDateFilter(startDate, endDate, props.station);
      if (newStartDateFilterErrors !== "") {
        setStartDateErrors(newStartDateFilterErrors);
      } else {
        setStartDateErrors("");
        setEndDateErrors("");
      }
      return newStartDateFilterErrors;
    } else {
      setStartDateErrors("");
      return "";
    }
  };

  useEffect(() => {
    validateEndDateWithStartDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);

  const validateEndDateWithStartDate = () => {
    if (startDate !== null && endDate !== null) {
      const newEndDateFilterErrors = validateEndDateFilter(endDate, startDate, props.station, false);
      if (newEndDateFilterErrors !== "") {
        setEndDateErrors(newEndDateFilterErrors);
      } else {
        setEndDateErrors("");
        setStartDateErrors("");
      }
      return newEndDateFilterErrors;
    } else {
      setEndDateErrors("");
      return "";
    }
  };

  // Generating Report Handlers
  const resetState = () => {
    setIsReportDownloading(false);
    setIsReportDownloadError(false);
    setResponseMessage("");
    setDownloadedFile(null);
  };

  const isFormValidationSuccess = () => {
    const dateTypeFormValidationErrors = validateDateType(dateType);
    setDateTypeErrors(dateTypeFormValidationErrors);

    const startDateFormValidationErrors = validateReportShiftDate(startDate);
    setStartDateErrors(startDateFormValidationErrors);

    let startDateWithEndDateFormValidationErrors = "";
    if (startDateFormValidationErrors === "") {
      startDateWithEndDateFormValidationErrors = validateStartDateWithEndDate();
      setStartDateErrors(startDateWithEndDateFormValidationErrors);
    }

    const endDateFormValidationErrors = validateReportShiftDate(endDate);
    setEndDateErrors(endDateFormValidationErrors);

    let endDateWithStartDateFormValidationErrors = "";
    if (endDateFormValidationErrors === "") {
      endDateWithStartDateFormValidationErrors = validateEndDateWithStartDate();
      setEndDateErrors(endDateWithStartDateFormValidationErrors);
    }

    const tradeTypeListFormValidationErrors = validateTradeTypeList(tradeTypeList);
    setTradeTypeListErrors(tradeTypeListFormValidationErrors);

    const statusListFormValidationErrors = validateStatusList(statusList);
    setStatusListErrors(statusListFormValidationErrors);

    const workgroupListFormValidationErrors = validateWorkgroupList(workgroupList);
    setWorkgroupListErrors(workgroupListFormValidationErrors);

    const employeeTradeRoleListFormValidationErrors = validateEmployeeTradeRoleList(
      employeeTradeRoleList,
      employeeData
    );
    setEmployeeTradeRoleListErrors(employeeTradeRoleListFormValidationErrors);

    const employeeIdFormValidationErrors = validateReportEmployeeId(employeeId, employeeTradeRoleList);
    setEmployeeIdErrors(employeeIdFormValidationErrors);

    let employeeDataFormValidationErrors = "";
    if (employeeIdFormValidationErrors === "" && employeeId !== "") {
      employeeDataFormValidationErrors = validateEmployeeDataForGeneratingReport(employeeData);
      setEmployeeIdErrors(employeeDataFormValidationErrors);
    }

    const adminIdFormValidationErrors = validateAdminId(adminId);
    setAdminIdErrors(adminIdFormValidationErrors);

    let adminEmployeeDataFormValidationErrors = "";
    if (adminIdFormValidationErrors === "" && adminId !== "") {
      adminEmployeeDataFormValidationErrors = validateAdminDataForGeneratingReport(adminEmployeeData);
      setAdminIdErrors(adminEmployeeDataFormValidationErrors);
    }

    return (
      dateTypeFormValidationErrors === "" &&
      startDateFormValidationErrors === "" &&
      startDateWithEndDateFormValidationErrors === "" &&
      endDateFormValidationErrors === "" &&
      endDateWithStartDateFormValidationErrors === "" &&
      tradeTypeListFormValidationErrors === "" &&
      statusListFormValidationErrors === "" &&
      workgroupListFormValidationErrors === "" &&
      employeeTradeRoleListFormValidationErrors === "" &&
      employeeIdFormValidationErrors === "" &&
      employeeDataFormValidationErrors === "" &&
      adminIdFormValidationErrors === "" &&
      adminEmployeeDataFormValidationErrors === ""
    );
  };

  const buildGenerateReportPayload = () => {
    return JSON.stringify({
      eid: userSession.eid,
      station: props.station,
      dateType: dateType,
      startDate: startDate?.toMillis().toString(),
      endDate: endDate.toMillis().toString(),
      tradeType: tradeTypeList,
      status: statusList,
      workgroup: workgroupList,
      empTradeRole: employeeTradeRoleList,
      employeeId: employeeId,
      adminId: adminId,
    });
  };

  const handleGenerateReport = () => {
    resetState();
    setIsReportDownloading(true);
    generateTradeDetailReport(buildGenerateReportPayload())
      .then((res) => res.json())
      .then(async (result) => {
        if (result.message) {
          setResponseMessage(result.message);
        } else {
          setDownloadedFile(result);
        }
        setIsReportDownloading(false);
      })
      .catch((err) => {
        setIsReportDownloadError(true);
        setIsReportDownloading(false);
      });
  };

  const handleCancelButtonClicked = () => {
    setIsCancelConfirmDialogOpen(true);
  };

  const handleCancelDialogNoButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
  };

  const handleCancelDialogYesButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
    props.onResetState();
  };

  const handleGenerateButtonClicked = () => {
    if (isFormValidationSuccess()) {
      setIsGenerateConfirmDialogOpen(true);
    }
  };

  const handleGenerateDialogNoButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
  };

  const handleGenerateDialogYesButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
    setIsGenerateDownloadDialogOpen(true);
    handleGenerateReport();
  };

  const handleDownloadDialogClose = () => {
    setIsGenerateDownloadDialogOpen(false);
  };

  const handleDownloadDialogDownloadButtonClicked = () => {
    const a = linkRef.current;
    a.download = downloadedFile.fileName;
    a.href = downloadedFile.downloadUrl;
    a.click();
    a.href = "";
    setIsGenerateDownloadDialogOpen(false);
    resetState();
    props.onResetState();
  };

  // Filter Input Handlers
  const fetchEmployeeData = (eid) => {
    setIsEmployeeDataLoading(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);

          let employeeDataErrors = validateEmployeeDataForGeneratingReport(employeeData);
          if (employeeDataErrors !== "") {
            setEmployeeIdErrors(employeeDataErrors);
          } else {
            setEmployeeData(employeeData);
          }
          setIsEmployeeDataLoading(false);
          setEmployeeTradeRoleListErrors(validateEmployeeTradeRoleList(employeeTradeRoleList, employeeData));
        } else {
          setEmployeeIdErrors("Invalid Employee ID");
          setIsEmployeeDataLoading(false);
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setEmployeeIdErrors("Invalid Employee ID");
        setIsEmployeeDataLoading(false);
      });
  };

  const fetchAdminEmployeeData = (eid) => {
    setIsAdminEmployeeDataLoading(true);
    setAdminIdErrors("");
    setAdminEmployeeData(null);
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);

          let employeeDataErrors = validateAdminDataForGeneratingReport(employeeData);
          if (employeeDataErrors !== "") {
            setAdminIdErrors(employeeDataErrors);
          } else {
            setAdminEmployeeData(employeeData);
          }
          setIsAdminEmployeeDataLoading(false);
        } else {
          setAdminIdErrors("Invalid Admin ID");
          setIsAdminEmployeeDataLoading(false);
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setAdminIdErrors("Invalid Admin ID");
        setIsAdminEmployeeDataLoading(false);
      });
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleEmployeeIdChange = (event) => {
    if (employeeData) {
      setEmployeeData(null);
    }
    setEmployeeId(event.target.value.toLowerCase());
    setIsEmployeeIdTouched(true);
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let inputErrors = validateReportEmployeeId(trimEmpId, employeeTradeRoleList);
    if (inputErrors === "") {
      if (
        trimEmpId !== "" &&
        (!employeeData || trimEmpId !== formatEmployeeIdFromKronos(employeeData.kronosPersonNumber))
      ) {
        fetchEmployeeData(trimEmpId);
        setEmployeeIdErrors("");
      }
    } else {
      setEmployeeIdErrors(inputErrors);
      setEmployeeTradeRoleListErrors(validateEmployeeTradeRoleList(employeeTradeRoleList, employeeData));
    }
  };

  const handleEmployeeIdClear = () => {
    setEmployeeData(null);
    setEmployeeId("");
    setEmployeeIdErrors(validateReportEmployeeId("", employeeTradeRoleList));
    setEmployeeTradeRoleListErrors(validateEmployeeTradeRoleList(employeeTradeRoleList, null));
  };

  const handleAdminIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleAdminIdBlur(event);
    }
  };

  const handleAdminIdChange = (event) => {
    if (adminEmployeeData) {
      setAdminEmployeeData(null);
    }
    setAdminId(event.target.value.toLowerCase());
  };

  const handleAdminIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let inputErrors = validateAdminId(trimEmpId);
    if (inputErrors === "") {
      if (
        trimEmpId !== "" &&
        (!adminEmployeeData || trimEmpId !== formatEmployeeIdFromKronos(adminEmployeeData.kronosPersonNumber))
      ) {
        fetchAdminEmployeeData(trimEmpId);
        setAdminIdErrors("");
      }
    } else {
      setAdminIdErrors(inputErrors);
    }
  };

  const handleAdminIdClear = () => {
    setAdminEmployeeData(null);
    setAdminId("");
    setAdminIdErrors("");
  };

  const handleDateTypeChange = (event) => {
    let inputErrors = validateDateType(event.target.value);
    if (inputErrors === "") {
      setDateType(event.target.value);
      setDateTypeErrors("");
    } else {
      setDateTypeErrors(inputErrors);
    }
  };

  const handleTradeTypeListChange = (event) => {
    let newTradeTypeFilterSelectedList = event.target.value;
    if (newTradeTypeFilterSelectedList.includes("Select All")) {
      if (isTradeTypeSelectAllChecked()) {
        newTradeTypeFilterSelectedList = [];
      } else {
        newTradeTypeFilterSelectedList = ALL_TRADE_TYPES;
      }
    }

    let inputErrors = validateTradeTypeList(newTradeTypeFilterSelectedList);
    setTradeTypeList(newTradeTypeFilterSelectedList);
    if (inputErrors === "") {
      setTradeTypeListErrors("");
    } else {
      setTradeTypeListErrors(inputErrors);
    }
  };

  const handleEmployeeTradeRoleListChange = (event) => {
    let newEmployeeTradeRoleSelectedList = event.target.value;
    if (newEmployeeTradeRoleSelectedList.includes("Select All")) {
      if (isEmployeeTradeRoleSelectAllChecked()) {
        newEmployeeTradeRoleSelectedList = [];
      } else {
        newEmployeeTradeRoleSelectedList = EMPLOYEE_TRADE_TYPES;
      }
    }

    let inputErrors = validateEmployeeTradeRoleList(newEmployeeTradeRoleSelectedList, employeeData);
    setEmployeeTradeRoleList(newEmployeeTradeRoleSelectedList);
    if (inputErrors === "") {
      setEmployeeTradeRoleListErrors("");
      if (isEmployeeIdTouched) {
        setEmployeeIdErrors(validateReportEmployeeId(employeeId, newEmployeeTradeRoleSelectedList));
      }
    } else {
      setEmployeeTradeRoleListErrors(inputErrors);
    }
  };

  const handleStartDateChange = (date) => {
    let inputErrors = validateReportShiftDate(date);
    if (inputErrors === "") {
      setStartDate(date);
      setStartDateErrors("");
    } else {
      setStartDateErrors(inputErrors);
    }
  };

  const handleEndDateChange = (date) => {
    const endOfDay = set_time_to_end_of_day(date);
    let inputErrors = validateReportShiftDate(endOfDay);
    if (inputErrors === "") {
      setEndDate(endOfDay);
      setEndDateErrors("");
    } else {
      setEndDateErrors(inputErrors);
    }
  };

  const handleWorkgroupListChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    if (newWorkgroupFilterSelectedList.includes("Select All")) {
      if (isWorkgroupSelectAllChecked()) {
        newWorkgroupFilterSelectedList = [];
      } else {
        newWorkgroupFilterSelectedList = getTradeWorkgroups(Object.keys(workgroupData));
      }
    }

    let inputErrors = validateWorkgroupList(newWorkgroupFilterSelectedList);
    setWorkgroupList(newWorkgroupFilterSelectedList);
    if (inputErrors === "") {
      setWorkgroupListErrors("");
    } else {
      setWorkgroupListErrors(inputErrors);
    }
  };

  const handleStatusListChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = ALL_STATUSES;
      }
    }

    let inputErrors = validateStatusList(newStatusFilterSelectedList);
    setStatusList(newStatusFilterSelectedList);
    if (inputErrors === "") {
      setStatusListErrors("");
    } else {
      setStatusListErrors(inputErrors);
    }
  };

  const renderEligibleWorkgroupMenuItems = () => {
    return getTradeWorkgroups(Object.keys(workgroupData))
      .slice()
      .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
      .map((workgroup) => {
        return (
          <MenuItem key={workgroup} value={workgroup}>
            <Checkbox color="primary" checked={workgroupList.indexOf(workgroup) > -1} />
            <ListItemText primary={workgroup} />
          </MenuItem>
        );
      });
  };

  const renderStatusMenuItems = () => {
    return ALL_STATUSES.slice()
      .sort()
      .map((status) => {
        return (
          <MenuItem key={status} value={status}>
            <Checkbox color="primary" checked={statusList.indexOf(status) > -1} />
            <ListItemText primary={status} />
          </MenuItem>
        );
      });
  };

  const renderTradeTypeFilterMenuItems = () => {
    return ALL_TRADE_TYPES.slice()
      .sort()
      .map((tradeType) => {
        return (
          <MenuItem key={tradeType} value={tradeType}>
            <Checkbox color="primary" checked={tradeTypeList.indexOf(tradeType) > -1} />
            <ListItemText primary={tradeType} />
          </MenuItem>
        );
      });
  };

  const renderEmployeeTradeRoleMenuItems = () => {
    return EMPLOYEE_TRADE_TYPES.slice()
      .sort()
      .map((employeeTradeRole) => {
        return (
          <MenuItem key={employeeTradeRole} value={employeeTradeRole}>
            <Checkbox color="primary" checked={employeeTradeRoleList.indexOf(employeeTradeRole) > -1} />
            <ListItemText primary={employeeTradeRole} />
          </MenuItem>
        );
      });
  };

  const isWorkgroupSelectAllIndeterminate = () => {
    return workgroupList.length !== getTradeWorkgroups(Object.keys(workgroupData)).length && workgroupList.length !== 0;
  };

  const isWorkgroupSelectAllChecked = () => {
    return workgroupList.length === getTradeWorkgroups(Object.keys(workgroupData)).length;
  };

  const isStatusSelectAllChecked = () => {
    return statusList.length === ALL_STATUSES.length;
  };

  const isStatusSelectAllIndeterminate = () => {
    return statusList.length !== ALL_STATUSES.length && statusList.length !== 0;
  };

  const isTradeTypeSelectAllChecked = () => {
    return tradeTypeList.length === ALL_TRADE_TYPES.length;
  };

  const isTradeTypeSelectAllIndeterminate = () => {
    return tradeTypeList.length !== ALL_TRADE_TYPES.length && tradeTypeList.length !== 0;
  };

  const isEmployeeTradeRoleSelectAllChecked = () => {
    return employeeTradeRoleList.length === EMPLOYEE_TRADE_TYPES.length;
  };

  const isEmployeeTradeRoleSelectAllIndeterminate = () => {
    return employeeTradeRoleList.length !== EMPLOYEE_TRADE_TYPES.length && employeeTradeRoleList.length !== 0;
  };

  return (
    <Grid item data-testid="trade-detail-report">
      <Grid item xs={12}>
        <FormControl variant="outlined" className={classes.control} error={dateTypeErrors !== ""}>
          <InputLabel id="date-type-label">Date Type</InputLabel>
          <Select
            labelId="date-type-label"
            className="select-field"
            id="dateTypeSelectElement"
            value={dateType}
            onChange={handleDateTypeChange}
            label="Date Type"
          >
            <MenuItem value="" disabled>
              <em>Date Type</em>
            </MenuItem>
            <MenuItem value="Approved Date">Approved Date</MenuItem>
            <MenuItem value="Employee #1 Shift Date">Employee #1 Shift Date</MenuItem>
            <MenuItem value="Employee #2 Shift Date">Employee #2 Shift Date</MenuItem>
            <MenuItem value="Last Updated Date">Last Updated Date</MenuItem>
            <MenuItem value="Shift Date">Shift Date</MenuItem>
            <MenuItem value="Submitted Date">Submitted Date</MenuItem>
          </Select>
          {dateTypeErrors !== "" && <FormHelperText error={true}>{dateTypeErrors}</FormHelperText>}
        </FormControl>
        <FormControl className={classes.control}>
          <StarsDatePicker
            label="Start Date"
            date={startDate}
            onChange={handleStartDateChange}
            dateErrors={startDateErrors}
            minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.station))}
            className={classes.input}
          />
        </FormControl>
        <FormControl className={classes.control}>
          <StarsDatePicker
            label="End Date"
            date={endDate}
            onChange={handleEndDateChange}
            dateErrors={endDateErrors}
            minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.station))}
            className={classes.input}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="outlined" className={classes.control} error={tradeTypeListErrors !== ""}>
          <InputLabel id="trade-type-label">Trade Type</InputLabel>
          <Select
            id="tradeFormTypeSelectElement"
            className={classes.filter}
            label="Trade Type"
            labelId="trade-type-label"
            multiple={true}
            value={tradeTypeList}
            onChange={handleTradeTypeListChange}
            renderValue={(selected) =>
              selected.length === ALL_TRADE_TYPES.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI"
            }
          >
            <MenuItem disabled>
              <em>Trade Type</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={isTradeTypeSelectAllChecked()}
                indeterminate={isTradeTypeSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderTradeTypeFilterMenuItems()}
          </Select>
          {tradeTypeListErrors !== "" && <FormHelperText error={true}>{tradeTypeListErrors}</FormHelperText>}
        </FormControl>
        <FormControl variant="outlined" className={classes.control} error={statusListErrors !== ""}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            id="statusFilterSelectElement"
            className={classes.filter}
            label="Status"
            labelId="status-label"
            multiple={true}
            value={statusList}
            onChange={handleStatusListChange}
            renderValue={(selected) =>
              selected.length === ALL_STATUSES.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI"
            }
          >
            <MenuItem disabled>
              <em>Status</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={isStatusSelectAllChecked()}
                indeterminate={isStatusSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderStatusMenuItems()}
          </Select>
          {statusListErrors !== "" && <FormHelperText error={true}>{statusListErrors}</FormHelperText>}
        </FormControl>
        <FormControl variant="outlined" className={classes.control} error={workgroupListErrors}>
          <InputLabel id="workgroup-label">Workgroup</InputLabel>
          <Select
            labelId="workgroup-label"
            id="workgroupFilterSelectElement"
            className={classes.filter}
            label="Workgroup"
            multiple={true}
            value={workgroupList}
            MenuProps={{ autoFocus: false }}
            onChange={handleWorkgroupListChange}
            renderValue={(selected) =>
              selected.length === getTradeWorkgroups(Object.keys(workgroupData)).length
                ? "ALL"
                : selected.length === 1
                ? selected[0]
                : "MULTI"
            }
          >
            <MenuItem disabled>
              <em>Workgroup</em>
            </MenuItem>
            <MenuItem key="select-all" value="Select All">
              <Checkbox
                color="primary"
                checked={isWorkgroupSelectAllChecked()}
                indeterminate={isWorkgroupSelectAllIndeterminate()}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {renderEligibleWorkgroupMenuItems()}
          </Select>
          {workgroupListErrors !== "" && <FormHelperText error={true}>{workgroupListErrors}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid container>
        <Grid item>
          <FormControl variant="outlined" className={classes.control} error={employeeTradeRoleListErrors !== ""}>
            <InputLabel id="employee-trade-role-label">Employee Trade Role</InputLabel>
            <Select
              id="employeeTradeRoleSelectElement"
              label="Employee Trade Role"
              labelId="employee-trade-role-label"
              className={classes.filter}
              value={employeeTradeRoleList}
              onChange={handleEmployeeTradeRoleListChange}
              multiple={true}
              renderValue={(selected) =>
                selected.length === EMPLOYEE_TRADE_TYPES.length ? "ALL" : selected.length === 1 ? selected[0] : "MULTI"
              }
            >
              <MenuItem disabled>
                <em>Employee Trade Role</em>
              </MenuItem>
              <MenuItem key="select-all" value="Select All">
                <Checkbox
                  color="primary"
                  checked={isEmployeeTradeRoleSelectAllChecked()}
                  indeterminate={isEmployeeTradeRoleSelectAllIndeterminate()}
                  indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                />
                <ListItemText primary="Select All" />
              </MenuItem>
              {renderEmployeeTradeRoleMenuItems()}
            </Select>
            {employeeTradeRoleListErrors !== "" && (
              <FormHelperText error={true}>{employeeTradeRoleListErrors}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl className={classes.control}>
            <TextField
              className="text-field"
              label="Employee ID"
              variant="outlined"
              value={employeeId}
              onChange={handleEmployeeIdChange}
              onBlur={handleEmployeeIdBlur}
              onKeyUp={handleEmployeeIdEnterKeyPressed}
              error={employeeIdErrors !== ""}
              InputProps={{
                endAdornment:
                  employeeId !== "" ? (
                    <InputAdornment position="start">
                      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeIdClear}>
                        <CancelIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
            />
            {employeeIdErrors !== "" && <FormHelperText error={true}>{employeeIdErrors}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item>
          {isEmployeeDataLoading && <CircularProgress className={classes.spinner} />}
          {employeeData !== null && (
            <Grid className="manage-access-employee-text" item xs={12}>
              <div>
                <span className="bold-text">Name:</span> {`${employeeData?.lastName}, ${employeeData?.firstName}`}
              </div>
              {employeeData.tempStation && (
                <div>
                  <span className="bold-text">Temp Station:</span> {employeeData.tempStation} (Active)
                </div>
              )}
              <div>
                <span className="bold-text">Home Station:</span> {employeeData.homeStation}
              </div>
              <div>
                <span className="bold-text">Workgroup:</span> {getWorkgroupGivenJobCode(employeeData?.jobCode)}
              </div>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item>
          <FormControl className={classes.control}>
            <TextField
              className="text-field"
              label="Admin ID"
              variant="outlined"
              value={adminId}
              onChange={handleAdminIdChange}
              onBlur={handleAdminIdBlur}
              onKeyUp={handleAdminIdEnterKeyPressed}
              error={adminIdErrors !== ""}
              InputProps={{
                endAdornment:
                  adminId !== "" ? (
                    <InputAdornment position="start">
                      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleAdminIdClear}>
                        <CancelIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
            />
            {adminIdErrors !== "" && <FormHelperText error={true}>{adminIdErrors}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item>
          {isAdminEmployeeDataLoading && <CircularProgress className={classes.spinner} />}
          {adminEmployeeData !== null && (
            <Grid className="manage-access-employee-text" item xs={12}>
              <div>
                <span className="bold-text">Name:</span>{" "}
                {`${adminEmployeeData?.lastName}, ${adminEmployeeData?.firstName}`}
              </div>
              {adminEmployeeData.tempStation && (
                <div>
                  <span className="bold-text">Temp Station:</span> {adminEmployeeData.tempStation} (Active)
                </div>
              )}
              <div>
                <span className="bold-text">Home Station:</span> {adminEmployeeData.homeStation}
              </div>
              <div>
                <span className="bold-text">Workgroup:</span> {getWorkgroupGivenJobCode(adminEmployeeData?.jobCode)}
              </div>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container item justifyContent="flex-end" spacing={2} xs={12} className={classes.buttonContainer}>
        <Grid item>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCancelButtonClicked}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={handleGenerateButtonClicked}
            size="large"
          >
            Generate
          </Button>
        </Grid>
      </Grid>
      {isCancelConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.SELECTIONS)}
          isDialogOpen={isCancelConfirmDialogOpen}
          handleNoButtonClicked={handleCancelDialogNoButtonClicked}
          handleYesButtonClicked={handleCancelDialogYesButtonClicked}
        />
      )}
      {isGenerateConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE}
          isDialogOpen={isGenerateConfirmDialogOpen}
          handleNoButtonClicked={handleGenerateDialogNoButtonClicked}
          handleYesButtonClicked={handleGenerateDialogYesButtonClicked}
        />
      )}
      {isGenerateDownloadDialogOpen && (
        <>
          <GenerateReportDownloadDialog
            isDownloading={isReportDownloading}
            isError={isReportDownloadError}
            isDialogOpen={isGenerateDownloadDialogOpen}
            onClose={handleDownloadDialogClose}
            onDownload={handleDownloadDialogDownloadButtonClicked}
            onRetry={handleGenerateReport}
            responseMessage={responseMessage}
          />
          <a className={classes.link} ref={linkRef} href="/">
            Download Report
          </a>
        </>
      )}
    </Grid>
  );
};

export default TradeDetailReport;
